// HR › Employees › the person › Pay: benefit-specific pay-rule exceptions (Report 55, OD-32). Every employee is covered
// unless an exception is approved. An exception names the benefit, the exception the rule lists, the person's actual
// duties and a start date; HR proposes it and the owner approves it. A job title or an Apparify role never decides it.
// The server decides who may propose, approve or withdraw; this only shows the records and sends the requests.
import { useCallback, useEffect, useId, useState } from "react";

import { Button, ConfirmDialog, Dialog, ErrorState, Field, Input, Notice, Select, SkeletonRows, Status, Surface, Textarea, plainError } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";

const day = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString("en-PH", { day: "numeric", month: "short", year: "numeric" }) : "");
const STATUS_WORD = { PENDING: "Waiting for the owner", APPROVED: "Approved", REJECTED: "Rejected", CANCELLED: "Withdrawn" };
const DUTIES_MIN = 40;

export function coverageErrors(form) {
  const errors = {};
  if (!form.benefit) errors.benefit = "Choose the benefit.";
  if (!form.exceptionCode) errors.exceptionCode = "Choose the exception that fits the person's duties.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(form.effectiveFrom || ""))) errors.effectiveFrom = "Choose the date it applies from.";
  const duties = String(form.duties || "").trim();
  if (duties.length < DUTIES_MIN) errors.duties = `Describe the person's actual duties (at least ${DUTIES_MIN} characters).`;
  else if (duties.length > 2000) errors.duties = "Keep this under 2,000 characters.";
  return errors;
}

function ProposeDialog({ memberId, memberName, exceptions, owner, onSaved, onClose }) {
  const formId = useId();
  const [form, setForm] = useState({ benefit: "", exceptionCode: "", effectiveFrom: "", duties: "" });
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [key]: value, ...(key === "benefit" ? { exceptionCode: "" } : {}) }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setFailure("");
  };
  const options = exceptions.find((item) => item.benefit === form.benefit)?.options || [];
  const chosen = options.find((item) => item.code === form.exceptionCode);

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const found = coverageErrors(form);
    setErrors(found);
    const first = ["benefit", "exceptionCode", "effectiveFrom", "duties"].find((key) => found[key]);
    if (first) { document.getElementById(`${formId}-${first}`)?.focus(); return; }
    setSaving(true);
    try {
      onSaved(await businessApi.proposeHRPayRuleCoverage(memberId, { ...form, duties: form.duties.trim() }));
      return;
    } catch (err) {
      setFailure(err?.status === 403 ? "You can't record pay-rule exceptions for this employee." : plainError(err, "The exception was not saved. Try again."));
    }
    setSaving(false);
  };

  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(open) => { if (!open && !saving) onClose(); }}
      title={`Record a pay-rule exception for ${memberName}`}
      description={owner ? "As the owner, your record is approved when saved." : "The owner approves it before payroll uses it. Until then the employee stays covered."}
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={saving}>{owner ? "Record and approve" : "Send to the owner"}</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Benefit" required id={`${formId}-benefit`} error={errors.benefit}>
            <Select value={form.benefit} onChange={set("benefit")} disabled={saving}>
              <option value="">Choose…</option>
              {exceptions.map((item) => <option key={item.benefit} value={item.benefit}>{item.label}</option>)}
            </Select>
          </Field>
          <Field label="From" required id={`${formId}-effectiveFrom`} error={errors.effectiveFrom} hint="Payroll already with Finance is not changed.">
            <Input type="date" value={form.effectiveFrom} onChange={set("effectiveFrom")} disabled={saving} />
          </Field>
        </div>
        <Field label="Exception" required id={`${formId}-exceptionCode`} error={errors.exceptionCode} hint={chosen ? `${chosen.conditions} (${chosen.sourceReference})` : "Only the exceptions the law lists for this benefit."}>
          <Select value={form.exceptionCode} onChange={set("exceptionCode")} disabled={saving || !form.benefit}>
            <option value="">{form.benefit ? "Choose…" : "Choose the benefit first"}</option>
            {options.map((item) => <option key={item.code} value={item.code}>{item.title}</option>)}
          </Select>
        </Field>
        <Field label="The person's actual duties" required id={`${formId}-duties`} error={errors.duties} hint="What they really do, measured against every condition above. A job title or Apparify role is not enough.">
          <Textarea rows={4} maxLength={2000} value={form.duties} onChange={set("duties")} disabled={saving} />
        </Field>
        {failure ? <Notice tone="danger" title="Not saved" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}

export default function PayRuleCoveragePanel({ memberId, memberName }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [proposing, setProposing] = useState(false);
  const [acting, setActing] = useState(null); // { record, action: "APPROVE" | "REJECT" | "CANCEL" }
  const [note, setNote] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await businessApi.getHRPayRuleCoverage(memberId));
      setError("");
    } catch (err) {
      setError(plainError(err, "The pay-rule exceptions couldn't be loaded."));
    }
  }, [memberId]);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <ErrorState title="Pay-rule exceptions couldn't be loaded" description={error} onRetry={load} />;
  if (!data) return <SkeletonRows rows={2} columns={2} label="Loading the pay-rule exceptions" />;

  const live = data.records.filter((row) => ["PENDING", "APPROVED"].includes(row.status));
  const past = data.records.filter((row) => !["PENDING", "APPROVED"].includes(row.status));
  const act = async () => {
    const { record, action } = acting;
    const result = action === "CANCEL"
      ? await businessApi.cancelHRPayRuleCoverage(record.id, { reason: note.trim() })
      : await businessApi.decideHRPayRuleCoverage(record.id, { decision: action, note: note.trim() || undefined });
    setData(result);
    setActing(null);
    setNote("");
    setNotice(action === "APPROVE" ? "Exception approved." : action === "REJECT" ? "Exception rejected." : "Exception withdrawn.");
  };

  return (
    <Surface as="section" aria-label="Pay-rule coverage" padded>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-section-title text-foreground">Pay-rule coverage</h2>
          <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">{data.notice}</p>
        </div>
        {data.canPropose ? <Button className="shrink-0" onClick={() => setProposing(true)}>Record an exception</Button> : null}
      </div>
      {notice ? <Notice tone="success" role="status" className="mt-3">{notice}</Notice> : null}
      {live.length ? (
        <ul className="mt-3 divide-y divide-subtle border-t border-subtle text-body-sm">
          {live.map((row) => (
            <li key={row.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
              <div className="min-w-0">
                <p className="font-medium text-foreground">Not covered by: {row.benefitLabel}</p>
                <p className="text-meta text-secondary">From {day(row.effectiveFrom)} · recorded by {row.recordedByName || "HR"}{row.decidedByName ? ` · approved by ${row.decidedByName}` : ""}</p>
                <p className="mt-0.5 max-w-[75ch] text-meta text-secondary">{row.duties}</p>
              </div>
              <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                {row.status === "PENDING" ? <Status tone="warning" icon>{STATUS_WORD[row.status]}</Status> : <span className="text-meta text-secondary">{STATUS_WORD[row.status]}</span>}
                <span className="flex flex-wrap gap-2">
                  {row.status === "PENDING" && data.canDecide ? (
                    <>
                      <Button size="sm" onClick={() => setActing({ record: row, action: "REJECT" })}>Reject</Button>
                      <Button size="sm" variant="primary" onClick={() => setActing({ record: row, action: "APPROVE" })}>Approve</Button>
                    </>
                  ) : null}
                  {(row.status === "PENDING" && data.canPropose) || (row.status === "APPROVED" && data.canDecide)
                    ? <Button size="sm" variant="quiet" onClick={() => setActing({ record: row, action: "CANCEL" })}>Withdraw</Button> : null}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : <p className="mt-3 text-body-sm text-foreground">Covered by overtime, rest-day premium and night shift differential. No exception is recorded.</p>}
      {past.length ? (
        <details className="mt-3 text-body-sm">
          <summary className="cursor-pointer rounded-control text-secondary max-md:flex max-md:min-h-touch max-md:items-center">Earlier records ({past.length})</summary>
          <ul className="mt-2 space-y-1 text-meta text-secondary">
            {past.map((row) => <li key={row.id}>{row.benefitLabel} from {day(row.effectiveFrom)} — {STATUS_WORD[row.status]}{row.decisionNote ? `: ${row.decisionNote}` : ""}</li>)}
          </ul>
        </details>
      ) : null}

      {proposing ? (
        <ProposeDialog
          memberId={memberId}
          memberName={memberName}
          exceptions={data.exceptions}
          owner={data.canDecide}
          onClose={() => setProposing(false)}
          onSaved={(result) => { setProposing(false); setData(result); setNotice(data.canDecide ? "Exception recorded and approved." : "Exception sent to the owner for approval."); }}
        />
      ) : null}
      {acting ? (
        <ConfirmDialog
          title={acting.action === "APPROVE" ? `Approve this exception for ${memberName}?` : acting.action === "REJECT" ? `Reject this exception for ${memberName}?` : `Withdraw this exception for ${memberName}?`}
          facts={[{ label: "Benefit", value: acting.record.benefitLabel }, { label: "From", value: day(acting.record.effectiveFrom) }]}
          consequence={acting.action === "APPROVE"
            ? "Payroll stops paying this benefit to the employee from that date. Payroll already with Finance is not changed."
            : "The employee stays covered by this benefit."}
          confirmLabel={acting.action === "APPROVE" ? "Approve exception" : acting.action === "REJECT" ? "Reject exception" : "Withdraw exception"}
          cancelLabel="Go back"
          tone={acting.action === "CANCEL" ? "danger" : "default"}
          confirmDisabled={acting.action !== "APPROVE" && note.trim().length < (acting.action === "REJECT" ? 5 : 3)}
          failureTitle="Not saved"
          onConfirm={act}
          onClose={() => { setActing(null); setNote(""); }}
        >
          <Field label={acting.action === "APPROVE" ? "Note (optional)" : "Reason"} required={acting.action !== "APPROVE"}>
            <Textarea rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </ConfirmDialog>
      ) : null}
    </Surface>
  );
}
